import { useParams } from "hooks/useParams";
import { OnboardingPage } from "./OnboardingPage";
import { parseBool } from "helpers/booleans";
import { loadStripe, SetupIntent } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { StripeElement } from "components/stripe/StripeElement";

export const OnboardingWrapper: React.FC = () => {
    const {
        planId,
        dts = "true",
        couponCode,
        resellerInventoryItemId,
        isTrialing,
        client_id,
        tag,
        returnScheme,
        setup_intent,
        setup_intent_client_secret,
        redirect_status
    } = useParams();

    const [setupIntent, setSetupIntent] = useState<SetupIntent | null>(null);
    const [registerBodyObject, setRegisterBodyObject] =
        useState<any>(undefined);
    // Only a user returning from a Stripe redirect (e.g. a bank
    // authorization page) needs to wait here; the SetupIntent used by a
    // fresh signup is now created later, on the card step itself.
    const [redirectResolved, setRedirectResolved] = useState(!setup_intent);

    useEffect(() => {
        if (!setup_intent) return;

        const retrieveRedirectSetupIntent = async () => {
            try {
                const stripe = await loadStripe(
                    import.meta.env.VITE_STRIPE_API_KEY
                );

                const { setupIntent } = await stripe.retrieveSetupIntent(
                    setup_intent_client_secret
                );

                const registerBodyJson =
                    localStorage.getItem("onboarding-body");
                localStorage.removeItem("onboarding-body");

                const registerBodyObject = !!registerBodyJson
                    ? JSON.parse(registerBodyJson)
                    : undefined;

                setSetupIntent(setupIntent);
                setRegisterBodyObject(registerBodyObject);
            } finally {
                setRedirectResolved(true);
            }
        };
        retrieveRedirectSetupIntent();
    }, [setup_intent, setup_intent_client_secret]);

    if (!redirectResolved) return null;

    return (
        // Deferred mode: OnboardingPage needs a Stripe Elements ancestor
        // from the moment it mounts (useOnboardingSetupIntent -> useMakePass
        // calls useStripe()), but the real SetupIntent for a fresh signup
        // isn't created until the card step is submitted (Turnstile-gated).
        // paymentMethodTypes must match what our backend's SetupIntent
        // endpoint actually creates (card only) - otherwise deferred mode's
        // automatic payment method detection can offer methods (e.g. bank)
        // that the real intent can't confirm.
        <StripeElement
            options={{
                mode: "setup",
                currency: "usd",
                paymentMethodTypes: ["card"],
                payment_method_types: ["card"]
            }}
        >
            <OnboardingPage
                planId={planId}
                defaultToSubscribe={parseBool(dts)}
                isTrialing={parseBool(isTrialing) ?? true}
                couponCode={
                    couponCode ?? import.meta.env.VITE_DEFAULT_DTS_COUPON
                }
                resellerInventoryItemId={resellerInventoryItemId}
                clientId={client_id ?? "SilverSunnDashboard"}
                tag={tag}
                returnScheme={returnScheme ?? null}
                setupIntent={setupIntent}
                redirectStatus={redirect_status}
                registerBody={registerBodyObject}
            />
        </StripeElement>
    );
};
